import React, { useEffect, useState } from 'react';
import {
  Card, Row, Col, Typography, Spin, Alert, Tag, Descriptions, Tabs, Table, Button, Statistic, Empty, message,
  Modal, Form, DatePicker, Input, Select, Space, Calendar
} from 'antd';
import { UserOutlined, CheckCircleOutlined, PlusOutlined, LeftOutlined, RightOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useAuth } from '../../../context/AuthContext';
import API from '../../../services/api';
import {
  fetchLeaves, applyLeave, cancelLeave, fetchMyPayslips, fetchAnnualEarnings, payslipPdfUrl,
  applyRegularization, fetchRegularizations, cancelRegularization, fetchRegularizationCalendar,
} from '../../hr/api';
import { updateProfileUser, changePassword } from '../api';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const money = (v) => `₹${Number(v || 0).toLocaleString('en-IN')}`;

const LEAVE_TYPE_OPTIONS = [
  { value: 'CASUAL', label: 'Casual Leave' },
  { value: 'SICK', label: 'Sick Leave' },
  { value: 'EARNED', label: 'Earned Leave' },
  { value: 'MATERNITY', label: 'Maternity Leave' },
  { value: 'PATERNITY', label: 'Paternity Leave' },
  { value: 'BEREAVEMENT', label: 'Bereavement Leave' },
  { value: 'LWP', label: 'Leave Without Pay' },
];

const REG_STATUS_OPTIONS = [
  { value: 'PENDING', label: 'Pending', color: 'processing' },
  { value: 'APPROVED', label: 'Approved', color: 'success' },
  { value: 'REJECTED', label: 'Rejected', color: 'error' },
  { value: 'CANCELLED', label: 'Cancelled', color: 'default' },
];

const LEAVE_STATUS_OPTIONS = [
  { value: 'PENDING_MANAGER', label: 'Pending Manager', color: 'processing' },
  { value: 'PENDING_HR', label: 'Pending HR', color: 'processing' },
  { value: 'APPROVED', label: 'Approved', color: 'success' },
  { value: 'REJECTED', label: 'Rejected', color: 'error' },
  { value: 'CANCELLED', label: 'Cancelled', color: 'default' },
];

const EMPLOYMENT_TYPE_OPTIONS = [
  { value: 'FULL_TIME', label: 'Full Time' },
  { value: 'PART_TIME', label: 'Part Time' },
  { value: 'CONTRACT', label: 'Contract' },
  { value: 'INTERN', label: 'Intern' },
];
const EMPLOYMENT_STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active', color: 'success' },
  { value: 'PROBATION', label: 'Probation', color: 'processing' },
  { value: 'NOTICE_PERIOD', label: 'Notice Period', color: 'warning' },
  { value: 'TERMINATED', label: 'Terminated', color: 'error' },
  { value: 'RESIGNED', label: 'Resigned', color: 'error' },
  { value: 'ABSCONDING', label: 'Absconding', color: 'error' },
];
const tagColor = (opts, val) => opts.find((o) => o.value === val)?.color || 'default';
const tagLabel = (opts, val) => opts.find((o) => o.value === val)?.label || val;

// Regularization calendar colors (see regularizationService.getRegularizationCalendar).
const REG_DAY_COLOR_HEX = { red: '#f5222d', green: '#389e0d', yellow: '#d4b106', orange: '#d46b08' };
const REG_CELL_BG    = { red: '#ffccc7', green: '#b7eb8f', yellow: '#ffe58f', orange: '#ffd591' };
const REG_CELL_TEXT  = { red: '#820014', green: '#135200', yellow: '#614700', orange: '#612500' };
const REG_LEGEND = [
  { color: 'red',    label: 'Absent (selectable)' },
  { color: 'green',  label: 'Present / Approved Leave' },
  { color: 'yellow', label: 'Leave — pending approval' },
  { color: 'orange', label: 'Late arrival — pending approval' },
];
// How far back the single-month calendar can navigate; mirrors the ~13-month
// look-back window regularizationService.getRegularizationCalendar queries.
const REG_CALENDAR_MONTHS_BACK = 13;

export default function ProfileView() {
  const { user, refreshProfile } = useAuth();
  const [checkingIn, setCheckingIn] = useState(false);
  
  // Leave States
  const [leaves, setLeaves] = useState([]);
  const [leavesLoading, setLeavesLoading] = useState(false);
  const [applyOpen, setApplyOpen] = useState(false);
  const [form] = Form.useForm();
  const [savingLeave, setSavingLeave] = useState(false);
  const selectedLeaveType = Form.useWatch('leaveType', form);

  // Regularization States
  const [regs, setRegs] = useState([]);
  const [regsLoading, setRegsLoading] = useState(false);
  const [regOpen, setRegOpen] = useState(false);
  const [regForm] = Form.useForm();
  const [savingReg, setSavingReg] = useState(false);
  const [regDayMap, setRegDayMap] = useState(new Map()); // 'YYYY-MM-DD' -> { color, disabled }
  const [regViewMonth, setRegViewMonth] = useState(() => dayjs().startOf('month'));
  const [regSelectedRange, setRegSelectedRange] = useState({ start: null, end: null });

  // Change Password States
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [pwdForm] = Form.useForm();
  const [savingPassword, setSavingPassword] = useState(false);

  // Payslip / annual-earning states
  const [payslips, setPayslips] = useState([]);
  const [annual, setAnnual] = useState(null);
  const [payLoading, setPayLoading] = useState(false);
  const [payYear, setPayYear] = useState(dayjs());

  const loadPayroll = async (yr) => {
    if (!user) return;
    const year = (yr || payYear).year();
    setPayLoading(true);
    try {
      const [slipRes, annRes] = await Promise.all([
        fetchMyPayslips({ year, limit: 100 }),
        fetchAnnualEarnings({ year }),
      ]);
      setPayslips(slipRes?.data?.data || []);
      setAnnual(annRes?.data?.data || null);
    } catch (err) {
      console.error('Payroll load error:', err?.response?.data || err.message);
    } finally {
      setPayLoading(false);
    }
  };

  const downloadPayslip = async (slip) => {
    try {
      const res = await API.get(payslipPdfUrl(slip._id), { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      message.error(err?.response?.data?.message || 'Could not open payslip');
    }
  };

  const loadLeaves = async () => {
    if (!user) return;
    setLeavesLoading(true);
    try {
      const res = await fetchLeaves({ employeeId: user.id || user._id, limit: 100 });
      setLeaves(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLeavesLoading(false);
    }
  };

  const loadRegularizations = async () => {
    if (!user) return;
    setRegsLoading(true);
    try {
      const res = await fetchRegularizations({ employeeId: user.id || user._id, limit: 100 });
      setRegs(res?.data?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setRegsLoading(false);
    }
  };

  const loadRegularizationCalendar = async () => {
    if (!user) return;
    try {
      const res = await fetchRegularizationCalendar({ employeeId: user.id || user._id });
      const map = new Map((res?.data?.data || []).map((d) => [d.date, { color: d.color, disabled: d.disabled }]));
      setRegDayMap(map);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    refreshProfile(); // Load latest profile on mount
    loadLeaves();
    loadPayroll();
    loadRegularizations();
    loadRegularizationCalendar();
  }, []);

  // Reset the picker to the current month + a blank selection every time the
  // modal opens (state lives on ProfileView, so it otherwise survives close).
  useEffect(() => {
    if (regOpen) {
      setRegViewMonth(dayjs().startOf('month'));
      setRegSelectedRange({ start: null, end: null });
    }
  }, [regOpen]);

  // A date is selectable only if it's in the currently-displayed month, in the
  // past, and colored red (absent/unmarked) & not already covered by a request.
  const isRegCalendarDisabled = (date) => {
    if (date.month() !== regViewMonth.month() || date.year() !== regViewMonth.year()) return true;
    if (!date.isBefore(dayjs().startOf('day'), 'day')) return true; // today & future
    const meta = regDayMap.get(date.format('YYYY-MM-DD'));
    return !(meta && meta.color === 'red' && !meta.disabled);
  };

  // Click-to-select: first click sets the start; a second, later click sets the
  // end (highlighting everything between); clicking the start again collapses
  // to a single day; clicking an earlier day restarts the selection.
  const handleRegDaySelect = (date, info) => {
    if (info.source !== 'date') return;
    setRegSelectedRange((prev) => {
      if (!prev.start || prev.end) return { start: date, end: null };
      if (date.isBefore(prev.start, 'day')) return { start: date, end: null };
      if (date.isSame(prev.start, 'day')) return { start: date, end: date };
      return { start: prev.start, end: date };
    });
  };

  // Full-cell colored render (uses fullCellRender so the background can cover
  // the whole cell including the date number — plain cellRender only inserts
  // content below antd's own date-number div).
  const regCalendarCellRender = (date, info) => {
    if (info.type !== 'date') return null;
    const inMonth = date.month() === regViewMonth.month() && date.year() === regViewMonth.year();
    const meta = regDayMap.get(date.format('YYYY-MM-DD'));
    const selectable = !isRegCalendarDisabled(date);
    const { start, end } = regSelectedRange;
    const isStart = !!start && date.isSame(start, 'day');
    const isEnd = !!end && date.isSame(end, 'day');
    const isInRange = !!start && !!end && date.isAfter(start, 'day') && date.isBefore(end, 'day');
    const isToday = date.isSame(dayjs(), 'day');

    const bg     = inMonth && meta?.color ? REG_CELL_BG[meta.color]   : 'transparent';
    const accent  = meta?.color ? REG_DAY_COLOR_HEX[meta.color]        : '#1677ff';
    // Force a dark, readable text colour for coloured cells so antd's built-in
    // "disabled" fade (colorTextDisabled ≈ rgba(0,0,0,0.25)) cannot wash it out.
    const textCol = inMonth && meta?.color ? REG_CELL_TEXT[meta.color] : undefined;

    // No onClick here — the antd/rc-picker cell wrapper this renders inside
    // already binds its own onClick gated by disabledDate, which drives the
    // Calendar's onSelect prop (handleRegDaySelect). Attaching a second click
    // handler here would fire both and double-toggle the selection per click.
    return (
      <div
        style={{
          height: 34, margin: '2px 3px', borderRadius: 8,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          position: 'relative',
          background: bg,
          border: (isStart || isEnd)
            ? `2px solid ${accent}`
            : isToday
              ? '1px dashed #8c8c8c'
              : (inMonth && meta?.color ? `1px solid ${accent}66` : '1px solid transparent'),
          opacity: inMonth ? 1 : 0.3,
          cursor: selectable ? 'pointer' : 'default',
          transition: 'background 0.15s, border-color 0.15s',
        }}
      >
        {isInRange && (
          <div style={{ position: 'absolute', inset: 2, background: 'rgba(22,119,255,0.25)', borderRadius: 6 }} />
        )}
        <span style={{
          position: 'relative', zIndex: 1, fontSize: 13,
          fontWeight: (isStart || isEnd) ? 700 : 500,
          color: textCol,             // overrides antd disabled-cell fade
        }}>
          {date.date()}
        </span>
      </div>
    );
  };

  const regCalendarHeaderRender = ({ value, onChange }) => {
    const thisMonth = dayjs().startOf('month');
    const floor = thisMonth.clone().subtract(REG_CALENDAR_MONTHS_BACK, 'month');
    const canPrev = value.isAfter(floor, 'month');
    const canNext = value.isBefore(thisMonth, 'month'); // never navigate into the future
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '4px 4px 12px' }}>
        <Button type="text" size="small" icon={<LeftOutlined />} disabled={!canPrev}
          onClick={() => onChange(value.clone().subtract(1, 'month'))} />
        <Text strong style={{ fontSize: 15 }}>{value.format('MMMM YYYY')}</Text>
        <Button type="text" size="small" icon={<RightOutlined />} disabled={!canNext}
          style={{ visibility: canNext ? 'visible' : 'hidden' }}
          onClick={() => onChange(value.clone().add(1, 'month'))} />
      </div>
    );
  };

  const submitRegularization = async () => {
    if (!regSelectedRange.start) {
      message.error('Please select a date or date range on the calendar');
      return;
    }
    try {
      const v = await regForm.validateFields();
      const from = regSelectedRange.start;
      const to = regSelectedRange.end || regSelectedRange.start;
      setSavingReg(true);
      await applyRegularization({
        employeeId: user.id || user._id,
        fromDate: from.startOf('day').toISOString(),
        toDate: to.startOf('day').toISOString(),
        reason: v.reason,
      });
      message.success('Regularization submitted for approval');
      setRegOpen(false);
      regForm.resetFields();
      setRegSelectedRange({ start: null, end: null });
      loadRegularizations();
      loadRegularizationCalendar();
    } catch (err) {
      if (!err?.errorFields) {
        const errorMsg = err?.response?.data?.message || 'Submission failed';
        message.error(errorMsg);
      }
    } finally {
      setSavingReg(false);
    }
  };

  const submitApply = async () => {
    try {
      const v = await form.validateFields();
      const [from, to] = v.range;
      const days = to.startOf('day').diff(from.startOf('day'), 'day') + 1;

      if (v.leaveType !== 'LWP') {
        const bal = (emp.leaveBalances || []).find(b => b.leaveType === v.leaveType);
        const available = bal ? (bal.allocated || 0) - (bal.used || 0) - (bal.pending || 0) : 0;
        if (days > available) {
          form.setFields([
            {
              name: 'range',
              errors: [`You requested ${days} day(s), but only have ${available} day(s) available.`],
            },
          ]);
          return;
        }
      }

      setSavingLeave(true);
      await applyLeave({
        employeeId: user.id || user._id,
        leaveType: v.leaveType, fromDate: from.toISOString(), toDate: to.toISOString(),
        halfDay: v.halfDay, reason: v.reason,
      });
      message.success('Leave applied successfully');
      setApplyOpen(false); 
      form.resetFields();
      loadLeaves();
      refreshProfile(); // To update pending balances
    } catch (err) {
      console.error('Leave Apply Error:', err?.response?.data || err.message);
      if (!err?.errorFields) {
        const errorMsg = err?.response?.data?.message || 'Apply failed';
        // Show error inside the modal form for better visibility
        form.setFields([
          {
            name: 'leaveType',
            errors: [errorMsg],
          },
        ]);
        message.error(errorMsg);
      }
    } finally {
      setSavingLeave(false);
    }
  };

  const submitPassword = async () => {
    try {
      const v = await pwdForm.validateFields();
      if (v.newPassword !== v.confirmPassword) {
        pwdForm.setFields([
          { name: 'confirmPassword', errors: ['Passwords do not match'] }
        ]);
        return;
      }
      setSavingPassword(true);
      const res = await changePassword({ password: v.newPassword });
      // The backend rotates the token on a password change (and drops any
      // "must change password" claim) — adopt it so the session stays current.
      if (res?.token) localStorage.setItem('token', res.token);
      message.success('Password changed successfully.');
      setPasswordOpen(false);
      pwdForm.resetFields();
    } catch (err) {
      console.error('Change password error:', err);
      const errorMsg = err?.response?.data?.message || 'Failed to change password';
      pwdForm.setFields([
        { name: 'newPassword', errors: [errorMsg] }
      ]);
      message.error(errorMsg);
    } finally {
      setSavingPassword(false);
    }
  };

  if (!user) {
    return <Spin tip="Loading profile..." style={{ display: 'block', marginTop: 80 }} />;
  }

  const emp = user;
  const att = emp.attendanceSummary || {};

  // Only trust todayAttendance if it is actually dated today. Otherwise a stale
  // record cached in localStorage from a previous day would flip the button to
  // "Check Out" and make the first click hit the wrong endpoint.
  const todayAtt = (emp.todayAttendance && dayjs(emp.todayAttendance.date).isSame(dayjs(), 'day'))
    ? emp.todayAttendance
    : null;

  const handleAttendance = async () => {
    if (checkingIn) return;
    try {
      setCheckingIn(true);
      const isCheckedIn = !!todayAtt?.checkIn;
      const endpoint = isCheckedIn ? '/hr/attendance/check-out' : '/hr/attendance/check-in';
      const res = await API.post(endpoint, {});
      message.success(res.data.message || `Successfully marked ${isCheckedIn ? 'day end' : 'attendance'}!`);
      await refreshProfile(); // await so the button reflects server truth on the next render
    } catch (err) {
      message.error(err.response?.data?.message || 'Attendance action failed');
    } finally {
      setCheckingIn(false);
    }
  };

  let availableBalanceText = null;
  if (selectedLeaveType && selectedLeaveType !== 'LWP') {
    const bal = (emp.leaveBalances || []).find(b => b.leaveType === selectedLeaveType);
    const available = bal ? (bal.allocated || 0) - (bal.used || 0) - (bal.pending || 0) : 0;
    availableBalanceText = <div style={{ color: available > 0 ? '#52c41a' : '#ff4d4f', fontSize: 13, marginTop: 4 }}>Available Balance: {available} day(s)</div>;
  }

  return (
    <div style={{ padding: '16px 24px', maxWidth: 1200, margin: '0 auto' }}>
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row justify="space-between" align="middle" gutter={[16, 16]}>
          <Col xs={24} sm={12}>
            <Title level={4} style={{ margin: 0 }}>
              <UserOutlined style={{ marginRight: 8 }} />{emp.name} {emp.employeeCode && <Text code>{emp.employeeCode}</Text>}
            </Title>
            <Text type="secondary">
              {emp.designation || '—'}
              {emp.department ? ` · ${emp.department}` : ''}
              {emp.branch?.name ? ` · ${emp.branch.name}` : ''}
            </Text>
          </Col>
          <Col xs={24} sm={12} style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
            {emp.employmentStatus && (
              <Tag color={tagColor(EMPLOYMENT_STATUS_OPTIONS, emp.employmentStatus)}>
                {tagLabel(EMPLOYMENT_STATUS_OPTIONS, emp.employmentStatus)}
              </Tag>
            )}
            {emp.employmentType && (
              <Tag>{tagLabel(EMPLOYMENT_TYPE_OPTIONS, emp.employmentType)}</Tag>
            )}
            {todayAtt?.lateArrivalStatus === 'PENDING' && (
              <Tag color="volcano">Late — pending approval</Tag>
            )}
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={handleAttendance}
              loading={checkingIn}
              disabled={!!todayAtt?.checkOut}
            >
              {todayAtt?.checkOut ? 'Attendance Marked' : todayAtt?.checkIn ? 'End Shift' : 'Mark Attendance'}
            </Button>
          </Col>
        </Row>
      </Card>

      <Tabs
        defaultActiveKey="personal"
        items={[
          {
            key: 'personal', label: 'Personal & Employment',
            children: (
              <Row gutter={[16, 16]}>
                <Col xs={24} md={12}>
                  <Descriptions title="Personal" size="small" column={1} bordered>
                    <Descriptions.Item label="Email">{emp.email}</Descriptions.Item>
                    <Descriptions.Item label="Phone">{emp.phone || '—'}</Descriptions.Item>
                    <Descriptions.Item label="DOB">{emp.dateOfBirth ? dayjs(emp.dateOfBirth).format('DD/MM/YYYY') : '—'}</Descriptions.Item>
                    <Descriptions.Item label="Gender">{emp.gender || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Marital Status">{emp.maritalStatus || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Blood Group">{emp.bloodGroup || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Aadhaar">{emp.adhar || '—'}</Descriptions.Item>
                    <Descriptions.Item label="PAN">{emp.pan || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Emergency Contact">{emp.emergencyContact?.name ? `${emp.emergencyContact.name} (${emp.emergencyContact.relationship || ''}) ${emp.emergencyContact.phone || ''}` : '—'}</Descriptions.Item>
                  </Descriptions>
                  <Button style={{ marginTop: 16 }} onClick={() => setPasswordOpen(true)}>Change Password</Button>
                </Col>
                <Col xs={24} md={12}>
                  <Descriptions title="Employment" size="small" column={1} bordered>
                    <Descriptions.Item label="Employee Code">{emp.employeeCode || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Joining Date">{emp.joiningDate ? dayjs(emp.joiningDate).format('DD/MM/YYYY') : '—'}</Descriptions.Item>
                    <Descriptions.Item label="Confirmation Date">{emp.confirmationDate ? dayjs(emp.confirmationDate).format('DD/MM/YYYY') : '—'}</Descriptions.Item>
                    <Descriptions.Item label="Reporting Manager">{emp.reportingManager?.name || '—'}</Descriptions.Item>
                    <Descriptions.Item label="Salary Grade">{emp.salaryGrade || '—'}</Descriptions.Item>
                    <Descriptions.Item label="PF / ESI">{(emp.pfNumber || '—')} / {(emp.esiNumber || '—')}</Descriptions.Item>
                    <Descriptions.Item label="Bank">{emp.bankDetails?.accountNumber ? `${emp.bankDetails.bankName || ''} ${emp.bankDetails.accountNumber} (${emp.bankDetails.ifsc || ''})` : '—'}</Descriptions.Item>
                  </Descriptions>
                </Col>
              </Row>
            ),
          },
          {
            key: 'attendance', label: 'Attendance & Leave',
            children: (
              <>
                <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                  <Col xs={8} sm={4}><Card size="small"><Statistic title="Present" value={att.present || 0} valueStyle={{ color: '#389e0d' }} /></Card></Col>
                  <Col xs={8} sm={4}><Card size="small"><Statistic title="Absent" value={att.absent || 0} valueStyle={{ color: '#cf1322' }} /></Card></Col>
                  <Col xs={8} sm={4}><Card size="small"><Statistic title="Leave" value={att.leave || 0} /></Card></Col>
                  <Col xs={8} sm={4}><Card size="small"><Statistic title="Half Day" value={att.halfDay || 0} /></Card></Col>
                  <Col xs={8} sm={4}><Card size="small"><Statistic title="Late Marks" value={att.lateMarks || 0} valueStyle={{ color: '#fa8c16' }} /></Card></Col>
                  <Col xs={8} sm={4}><Card size="small"><Statistic title="Payable Days" value={att.payableDays || 0} /></Card></Col>
                </Row>
                
                <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
                  <Col><Title level={5}>Leave Applications</Title></Col>
                  <Col><Button type="primary" icon={<PlusOutlined />} onClick={() => setApplyOpen(true)}>Apply Leave</Button></Col>
                </Row>
                <Table size="small" rowKey="_id" dataSource={leaves} loading={leavesLoading} scroll={{ x: 'max-content' }}
                  columns={[
                    { title: 'Ref', dataIndex: 'reference', render: (v) => <Text code>{v}</Text> },
                    { title: 'Type', dataIndex: 'leaveType', render: (v) => tagLabel(LEAVE_TYPE_OPTIONS, v) },
                    { title: 'From', dataIndex: 'fromDate', render: (v) => dayjs(v).format('DD/MM/YYYY') },
                    { title: 'To', dataIndex: 'toDate', render: (v) => dayjs(v).format('DD/MM/YYYY') },
                    { title: 'Days', dataIndex: 'days' },
                    { 
                      title: 'Status', 
                      dataIndex: 'status', 
                      render: (v, r) => (
                        <Space direction="vertical" size={0}>
                          <Tag color={tagColor(LEAVE_STATUS_OPTIONS, v)}>{tagLabel(LEAVE_STATUS_OPTIONS, v)}</Tag>
                          {v === 'PENDING_MANAGER' && (
                            <Text type="secondary" style={{ fontSize: 11 }}>with {r.reportingManager?.name || 'Manager'}</Text>
                          )}
                          {v === 'PENDING_HR' && (
                            <Text type="secondary" style={{ fontSize: 11 }}>with HR Admin</Text>
                          )}
                        </Space>
                      )
                    },
                    { title: 'Actions', render: (_, r) => (
                        ['PENDING_MANAGER', 'PENDING_HR'].includes(r.status) && (
                          <Button size="small" danger onClick={async () => {
                            try {
                              await cancelLeave(r._id);
                              message.success('Leave cancelled');
                              loadLeaves();
                              refreshProfile();
                            } catch (e) {
                              message.error(e?.response?.data?.message || 'Cancel failed');
                            }
                          }}>Cancel</Button>
                        )
                      )
                    },
                  ]}
                  locale={{ emptyText: 'No leaves applied' }}
                  style={{ marginBottom: 16 }}
                />

                <Card size="small" title="Leave Balances (this year)">
                  <Table size="small" rowKey={(r) => r._id || r.leaveType} pagination={false} dataSource={emp.leaveBalances || []} scroll={{ x: 'max-content' }}
                    columns={[
                      { title: 'Type', dataIndex: 'leaveType' },
                      { title: 'Allocated', dataIndex: 'allocated', align: 'right' },
                      { title: 'Used', dataIndex: 'used', align: 'right' },
                      { title: 'Pending', dataIndex: 'pending', align: 'right' },
                      { title: 'Available', dataIndex: 'available', align: 'right', render: (v, r) => (r.allocated || 0) - (r.used || 0) - (r.pending || 0) },
                    ]}
                    locale={{ emptyText: 'No balances yet' }}
                  />
                </Card>

                <Row justify="space-between" align="middle" style={{ margin: '16px 0 12px' }}>
                  <Col>
                    <Title level={5} style={{ margin: 0 }}>Attendance Regularization</Title>
                    <Text type="secondary" style={{ fontSize: 12 }}>Request past absent/unmarked working days to be marked Present.</Text>
                  </Col>
                  <Col><Button icon={<PlusOutlined />} onClick={() => setRegOpen(true)}>Request Regularization</Button></Col>
                </Row>
                <Table size="small" rowKey="_id" dataSource={regs} loading={regsLoading} scroll={{ x: 'max-content' }}
                  columns={[
                    { title: 'Ref', dataIndex: 'reference', render: (v) => <Text code>{v}</Text> },
                    { title: 'From', dataIndex: 'fromDate', render: (v) => dayjs(v).format('DD/MM/YYYY') },
                    { title: 'To', dataIndex: 'toDate', render: (v) => dayjs(v).format('DD/MM/YYYY') },
                    { title: 'Reason', dataIndex: 'reason', render: (v) => v || '—' },
                    {
                      title: 'Status', dataIndex: 'status',
                      render: (v, r) => (
                        <Space direction="vertical" size={0}>
                          <Tag color={tagColor(REG_STATUS_OPTIONS, v)}>{tagLabel(REG_STATUS_OPTIONS, v)}</Tag>
                          {v === 'PENDING' && r.currentStepName && (
                            <Text type="secondary" style={{ fontSize: 11 }}>at {r.currentStepName}</Text>
                          )}
                          {v === 'APPROVED' && (
                            <Text type="secondary" style={{ fontSize: 11 }}>{(r.regularizedDates || []).length} day(s) marked Present</Text>
                          )}
                        </Space>
                      ),
                    },
                    {
                      title: 'Actions', render: (_, r) => (
                        r.status === 'PENDING' && (
                          <Button size="small" danger onClick={async () => {
                            try {
                              await cancelRegularization(r._id);
                              message.success('Regularization cancelled');
                              loadRegularizations();
                              loadRegularizationCalendar();
                            } catch (e) {
                              message.error(e?.response?.data?.message || 'Cancel failed');
                            }
                          }}>Cancel</Button>
                        )
                      ),
                    },
                  ]}
                  locale={{ emptyText: 'No regularization requests' }}
                  style={{ marginTop: 8 }}
                />
              </>
            ),
          },
          {
            key: 'salary', label: 'Salary & Payslips',
            children: (
              <>
                <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
                  <Col><Title level={5} style={{ margin: 0 }}>Annual Earnings</Title></Col>
                  <Col>
                    <DatePicker picker="year" allowClear={false} value={payYear}
                      onChange={(d) => { const v = d || dayjs(); setPayYear(v); loadPayroll(v); }} />
                  </Col>
                </Row>
                <Row gutter={[12, 12]} style={{ marginBottom: 16 }}>
                  <Col xs={12} sm={6}><Card size="small"><Statistic title="Gross (YTD)" value={annual?.totals?.grossEarnings || 0} prefix="₹" precision={2} valueStyle={{ color: '#389e0d' }} /></Card></Col>
                  <Col xs={12} sm={6}><Card size="small"><Statistic title="Deductions (YTD)" value={annual?.totals?.totalDeductions || 0} prefix="₹" precision={2} valueStyle={{ color: '#cf1322' }} /></Card></Col>
                  <Col xs={12} sm={6}><Card size="small"><Statistic title="Net Paid (YTD)" value={annual?.totals?.netPay || 0} prefix="₹" precision={2} valueStyle={{ color: '#0a58ca' }} /></Card></Col>
                  <Col xs={12} sm={6}><Card size="small"><Statistic title="Slips" value={annual?.totals?.months || 0} /></Card></Col>
                </Row>
                <Card size="small" title="Monthly Payslips">
                  <Table size="small" rowKey="_id" dataSource={payslips} loading={payLoading} pagination={false} scroll={{ x: 'max-content' }}
                    columns={[
                      { title: 'Ref', dataIndex: 'reference', render: (v) => <Text code>{v}</Text> },
                      { title: 'Month', render: (_, r) => dayjs(new Date(r.year, r.month - 1, 1)).format('MMM YYYY') },
                      { title: 'Payable Days', dataIndex: ['attendance', 'payableDays'], align: 'right' },
                      { title: 'Gross', dataIndex: 'grossEarnings', align: 'right', render: money },
                      { title: 'Deductions', dataIndex: 'totalDeductions', align: 'right', render: money },
                      { title: 'Net Pay', dataIndex: 'netPay', align: 'right', render: (v) => <Text strong>{money(v)}</Text> },
                      { title: 'Status', dataIndex: 'status', render: (v) => <Tag color={v === 'PAID' ? 'success' : v === 'FINALIZED' ? 'blue' : 'default'}>{v}</Tag> },
                      { title: '', render: (_, r) => <Button size="small" type="link" onClick={() => downloadPayslip(r)}>Download</Button> },
                    ]}
                    locale={{ emptyText: 'No payslips available yet — they appear here once payroll is finalized' }}
                  />
                </Card>
              </>
            ),
          },
          {
            key: 'assets', label: `Assets (${emp.assignedAssets?.length || 0})`,
            children: (
              <Table size="small" rowKey="_id" dataSource={emp.assignedAssets || []} pagination={false} scroll={{ x: 'max-content' }}
                columns={[
                  { title: 'Code', dataIndex: 'assetCode', render: (v) => <Text code>{v}</Text> },
                  { title: 'Name', dataIndex: 'name' },
                  { title: 'Category', dataIndex: 'category', render: (v) => <Tag>{v?.replace(/_/g, ' ')}</Tag> },
                ]}
                locale={{ emptyText: 'No assets assigned' }}
              />
            ),
          },
          {
            key: 'documents', label: `Documents (${emp.documents?.length || 0})`,
            children: (
              <Table size="small" rowKey="_id" dataSource={emp.documents || []} pagination={false} scroll={{ x: 'max-content' }}
                columns={[
                  { title: 'Type', dataIndex: 'docType', render: (v) => <Tag>{v?.replace(/_/g, ' ')}</Tag> },
                  { title: 'Title', dataIndex: 'title', render: (v) => v || '—' },
                  { title: 'Expiry', dataIndex: 'expiryDate', render: (v) => v ? dayjs(v).format('DD/MM/YYYY') : '—' },
                  { title: 'File', dataIndex: 'fileUrl', render: (v) => v ? <a href={v} target="_blank" rel="noreferrer">View</a> : '—' },
                ]}
                locale={{ emptyText: 'No documents' }}
              />
            ),
          },
          {
            key: 'performance', label: 'Performance',
            children: (
              <Table size="small" rowKey="_id" dataSource={emp.performance || []} pagination={false} scroll={{ x: 'max-content' }}
                columns={[
                  { title: 'Period', dataIndex: 'periodKey' },
                  { title: 'Target', dataIndex: 'monthlyTarget', align: 'right', render: money },
                  { title: 'Revenue', dataIndex: 'revenueGenerated', align: 'right', render: money },
                  { title: 'Cases', dataIndex: 'casesClosed', align: 'right' },
                  { title: 'Gold (g)', dataIndex: 'goldPurchasedGrams', align: 'right' },
                  { title: 'Achieve %', dataIndex: 'achievementPercent', align: 'right', render: (v) => `${v || 0}%` },
                  { title: 'Rating', dataIndex: 'rating', align: 'right', render: (v) => v ? `${v}/5` : '—' },
                ]}
                locale={{ emptyText: 'No reviews' }}
              />
            ),
          },
        ]}
      />

      {emp.activeExit && (
        <Alert style={{ marginTop: 16 }} type="warning" showIcon
          message={`Active exit process: ${emp.activeExit.reference} (${emp.activeExit.status})`}
          description={emp.activeExit.lastWorkingDate ? `Last working date: ${dayjs(emp.activeExit.lastWorkingDate).format('DD/MM/YYYY')}` : null}
        />
      )}

      <Modal title="Apply Leave" open={applyOpen} onCancel={() => setApplyOpen(false)} onOk={submitApply} okButtonProps={{ loading: savingLeave }} destroyOnClose>
        <Form form={form} layout="vertical">
          <Form.Item name="leaveType" label="Leave Type" rules={[{ required: true }]} style={{ marginBottom: availableBalanceText ? 8 : 24 }}>
            <Select options={LEAVE_TYPE_OPTIONS.filter(opt => opt.value === 'LWP' || (emp.leaveBalances || []).some(b => b.leaveType === opt.value))} />
          </Form.Item>
          {availableBalanceText && <div style={{ marginBottom: 24 }}>{availableBalanceText}</div>}
          <Form.Item name="range" label="From – To" rules={[{ required: true }]}><RangePicker style={{ width: '100%' }} /></Form.Item>
          <Form.Item name="reason" label="Reason"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>

      <Modal title="Request Attendance Regularization" open={regOpen} onCancel={() => setRegOpen(false)} onOk={submitRegularization} okButtonProps={{ loading: savingReg }} okText="Submit" destroyOnClose width={480}>
        <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 8 }}>
          Click a red day to start, click another red day to finish the range, or click the same day again for a single day.
        </Text>
        <div style={{ border: '1px solid #f0f0f0', borderRadius: 10, padding: '8px 12px', marginBottom: 12 }}>
          <Calendar
            fullscreen={false}
            mode="month"
            value={regViewMonth}
            onChange={setRegViewMonth}
            onSelect={handleRegDaySelect}
            disabledDate={isRegCalendarDisabled}
            headerRender={regCalendarHeaderRender}
            fullCellRender={regCalendarCellRender}
          />
        </div>

        {regSelectedRange.start && (
          <div style={{ marginBottom: 12, fontSize: 13 }}>
            <Text type="secondary">Selected: </Text>
            <Text strong>{regSelectedRange.start.format('DD MMM YYYY')}</Text>
            {regSelectedRange.end && !regSelectedRange.end.isSame(regSelectedRange.start, 'day') && (
              <>
                <Text type="secondary"> to </Text>
                <Text strong>{regSelectedRange.end.format('DD MMM YYYY')}</Text>
              </>
            )}
          </div>
        )}

        <Space size={16} wrap style={{ marginBottom: 16 }}>
          {REG_LEGEND.map((l) => (
            <Space key={l.color} size={6}>
              <span style={{
                display: 'inline-block', width: 10, height: 10, borderRadius: '50%',
                background: REG_CELL_BG[l.color],
                border: `2px solid ${REG_DAY_COLOR_HEX[l.color]}`,
                flexShrink: 0,
              }}
              />
              <Text style={{ fontSize: 12, color: REG_CELL_TEXT[l.color], fontWeight: 500 }}>{l.label}</Text>
            </Space>
          ))}
        </Space>

        <Form form={regForm} layout="vertical">
          <Form.Item name="reason" label="Reason" rules={[{ required: true, message: 'Please give a reason' }]}>
            <Input.TextArea rows={3} placeholder="e.g. Forgot to check in, on-site client visit, network issue" />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title="Change Password" open={passwordOpen} onCancel={() => setPasswordOpen(false)} onOk={submitPassword} okButtonProps={{ loading: savingPassword }} destroyOnClose>
        <Form form={pwdForm} layout="vertical">
          <Form.Item name="newPassword" label="New Password" rules={[{ required: true }]}>
            <Input.Password />
          </Form.Item>
          <Form.Item name="confirmPassword" label="Confirm Password" rules={[{ required: true }]}>
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
